import {
  Button,
  Flex,
  FormControl,
  Grid,
  Input,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
  Select,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { ChangeEvent, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SimpleFileLink } from '@texas/api/endpoints/filesApi';
import {
  ComplianceDocumentDefinitionRequest,
  ComplianceDocumentKind,
  ComplianceDocumentType,
} from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { EntityDirectoryFileThumbnail } from '@texas/components/shared/entityDirectory/EntityDirectoryFileThumbnail';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { FileUploads } from '@texas/components/shared/dropzone/FileUploads';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { complianceDocumentTypeStyles } from '@texas/resources/theme/styles/configuration';
import { ServerError } from '@texas/types';
import {
  ensureEnumNumber,
  getEnumValues,
} from '@texas/utils/helpers/enumHelpers';
import {
  complianceDocumentKindLabel,
  complianceDocumentTypeHelp,
  complianceDocumentTypeLabel,
} from './translations';

const maxLogoSize = 5_000_000;
const acceptedLogoTypes = 'image/jpeg,image/png,image/svg+xml';
const complianceDocumentKinds = getEnumValues<ComplianceDocumentKind>(
  ComplianceDocumentKind,
);
const complianceDocumentTypes = getEnumValues<ComplianceDocumentType>(
  ComplianceDocumentType,
);

interface Props {
  defaultValues: ComplianceDocumentDefinitionRequest;
  error: ServerError | null;
  folderId: number;
  initialLogo: SimpleFileLink | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (request: ComplianceDocumentDefinitionRequest) => Promise<void>;
  submitLabel: string;
}

export function ComplianceDocumentDefinitionForm({
  defaultValues,
  error,
  folderId,
  initialLogo,
  isSubmitting,
  onClose,
  onSubmit,
  submitLabel,
}: Props) {
  const { t } = useTranslation();
  const logoInput = useRef<HTMLInputElement>(null);
  const [logo, setLogo] = useState<SimpleFileLink | null>(initialLogo);
  const formDefaultValues = {
    ...defaultValues,
    kind: ensureEnumNumber(ComplianceDocumentKind, defaultValues.kind),
    type: ensureEnumNumber(ComplianceDocumentType, defaultValues.type),
  };
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<ComplianceDocumentDefinitionRequest>({
    defaultValues: formDefaultValues,
  });
  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
    resetFileUploads,
  } = useFileUploads((file) => {
    setLogo(file);
    setValue('logoFileId', file.id, { shouldDirty: true });
  }, maxLogoSize);

  const close = () => {
    reset(formDefaultValues);
    setLogo(initialLogo);
    resetFileUploads();
    onClose();
  };

  const uploadLogo = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) uploadFiles(files, folderId);
    event.target.value = '';
  };

  const removeLogo = () => {
    setLogo(null);
    setValue('logoFileId', null, { shouldDirty: true });
    resetFileUploads();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalBody>
        <ErrorDetails error={error} />
        <Flex direction="column" gap={5}>
          <Grid
            alignItems="start"
            gap={3}
            gridTemplateColumns={{
              base: '1fr',
              md: 'minmax(0, 1fr) 210px',
            }}
          >
            <FormControl isRequired={true} isInvalid={!!errors.name}>
              <TexasFormLabel>{t('general.name')}</TexasFormLabel>
              <Input
                variant="entityDirectory"
                placeholder={t('complianceDocumentDefinitions.namePlaceholder')}
                {...register('name', {
                  required: true,
                  maxLength: {
                    value: 100,
                    message: t('errors.maxLength', { count: 100 }),
                  },
                })}
              />
              <HookFormCounter control={control} name="name" maxLength={100} />
              <ErrorLabel text={errors.name?.message} />
            </FormControl>
            <FormControl isRequired={true} isInvalid={!!errors.kind}>
              <TexasFormLabel>
                {t('complianceDocumentDefinitions.kindLabel')}
              </TexasFormLabel>
              <Select
                size="sm"
                variant="entityDirectory"
                {...register('kind', { required: true, valueAsNumber: true })}
              >
                {complianceDocumentKinds.map((kind) => (
                  <option key={kind} value={kind}>
                    {complianceDocumentKindLabel(kind, t)}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <FormControl isRequired={true} isInvalid={!!errors.description}>
            <TexasFormLabel>{t('general.description')}</TexasFormLabel>
            <Textarea
              minH="92px"
              resize="vertical"
              variant="entityDirectory"
              placeholder={t(
                'complianceDocumentDefinitions.descriptionPlaceholder',
              )}
              {...register('description', {
                required: true,
                maxLength: {
                  value: 1000,
                  message: t('errors.maxLength', { count: 1000 }),
                },
              })}
            />
            <HookFormCounter
              control={control}
              name="description"
              maxLength={1000}
            />
            <ErrorLabel text={errors.description?.message} />
          </FormControl>
          <FormControl
            as="fieldset"
            isRequired={true}
            isInvalid={!!errors.type}
          >
            <TexasFormLabel as="legend">
              {t('complianceDocumentDefinitions.typeLabel')}
            </TexasFormLabel>
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <RadioGroup
                  value={String(field.value)}
                  onChange={(value) =>
                    field.onChange(Number(value) as ComplianceDocumentType)
                  }
                >
                  <Grid sx={complianceDocumentTypeStyles.options}>
                    {complianceDocumentTypes.map((type) => (
                      <Radio key={type} value={String(type)}>
                        <Flex direction="column">
                          <Text variant="configuration-strong">
                            {complianceDocumentTypeLabel(type, t)}
                          </Text>
                          <Text variant="configuration-meta">
                            {complianceDocumentTypeHelp(type, t)}
                          </Text>
                        </Flex>
                      </Radio>
                    ))}
                  </Grid>
                </RadioGroup>
              )}
            />
          </FormControl>
          <FormControl>
            <TexasFormLabel>
              {t('complianceDocumentDefinitions.logo')}
            </TexasFormLabel>
            <Text mb={2} variant="entity-directory-description">
              {t('complianceDocumentDefinitions.logoHelp')}
            </Text>
            <Input
              ref={logoInput}
              type="file"
              display="none"
              accept={acceptedLogoTypes}
              onChange={uploadLogo}
            />
            <Flex
              align="center"
              bg="entityDirectory.raisedSurface"
              border="1px solid"
              borderColor="entityDirectory.border"
              gap={3}
              minH="92px"
              p={3}
            >
              {logo ? (
                <>
                  <EntityDirectoryFileThumbnail file={logo} alt="" />
                  <Flex direction="column" flex="1" minW="0">
                    <Text variant="configuration-strong" noOfLines={1}>
                      {logo.name}
                    </Text>
                    <Text variant="configuration-meta">
                      {logo.extension.toUpperCase()}
                    </Text>
                  </Flex>
                  <Button
                    type="button"
                    variant="entityDirectorySecondary"
                    onClick={() => logoInput.current?.click()}
                  >
                    {t('general.replace')}
                  </Button>
                  <Button
                    type="button"
                    variant="entityDirectorySecondary"
                    onClick={removeLogo}
                  >
                    {t('general.remove')}
                  </Button>
                </>
              ) : (
                <>
                  <EntityDirectoryFileThumbnail file={null} alt="" />
                  <Flex direction="column" flex="1">
                    <Text variant="configuration-strong">
                      {t('complianceDocumentDefinitions.uploadLogo')}
                    </Text>
                    <Text variant="configuration-meta">
                      {t('complianceDocumentDefinitions.logoFormats')}
                    </Text>
                  </Flex>
                  <Button
                    type="button"
                    variant="entityDirectorySecondary"
                    isLoading={isUploading}
                    onClick={() => logoInput.current?.click()}
                  >
                    {t('complianceDocumentDefinitions.chooseFile')}
                  </Button>
                </>
              )}
            </Flex>
            {(isUploading || uploadFailed) && (
              <FileUploads
                fileUploads={fileUploads}
                removeFileUpload={removeFileUpload}
              />
            )}
          </FormControl>
        </Flex>
      </ModalBody>
      <ModalFooter gap={2}>
        <Button
          type="button"
          variant="entityDirectorySecondary"
          onClick={close}
        >
          {t('general.cancel')}
        </Button>
        <Button
          type="submit"
          variant="entityDirectoryPrimary"
          isLoading={isSubmitting}
          isDisabled={isUploading || uploadFailed}
        >
          {submitLabel}
        </Button>
      </ModalFooter>
    </form>
  );
}
